import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';


const TicketAssignment = ({ 
  tickets = [], 
  assignments = {}, 
  onUpdateAssignment = () => {},
  errors = {}
}) => {
  const [expandedTickets, setExpandedTickets] = useState(new Set());

  const toggleTicketExpansion = (ticketId) => {
    const newExpanded = new Set(expandedTickets);
    if (newExpanded?.has(ticketId)) {
      newExpanded?.delete(ticketId);
    } else {
      newExpanded?.add(ticketId);
    }
    setExpandedTickets(newExpanded);
  };

  const handleAssignmentChange = (ticketId, index, field, value) => {
    const currentAssignments = assignments?.[ticketId] || [];
    const updatedAssignments = [...currentAssignments];
    
    if (!updatedAssignments?.[index]) {
      updatedAssignments[index] = {};
    }
    
    updatedAssignments[index] = {
      ...updatedAssignments?.[index],
      [field]: value
    };
    
    onUpdateAssignment(ticketId, updatedAssignments);
  };

  const getTicketAssignments = (ticketId) => {
    return assignments?.[ticketId] || [];
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation-2 p-6">
      <div className="flex items-center space-x-2 mb-6">
        <Icon name="Users" size={20} className="text-primary" />
        <h2 className="text-lg font-semibold text-foreground">Ticket Assignment</h2>
      </div>
      <div className="space-y-4">
        {tickets?.map((ticket) => {
          const isExpanded = expandedTickets?.has(ticket?.id);
          const ticketAssignments = getTicketAssignments(ticket?.id);
          
          return (
            <div key={ticket?.id} className="border border-border rounded-lg">
              <button
                onClick={() => toggleTicketExpansion(ticket?.id)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-muted transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                    <Icon name="Ticket" size={20} color="white" />
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground">{ticket?.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {ticket?.quantity} ticket{ticket?.quantity > 1 ? 's' : ''} • ${ticket?.price} each
                    </p>
                  </div>
                </div>
                <Icon 
                  name={isExpanded ? "ChevronUp" : "ChevronDown"} 
                  size={20} 
                  className="text-muted-foreground" 
                />
              </button>
              {isExpanded && (
                <div className="px-4 pb-4 border-t border-border">
                  <p className="text-sm text-muted-foreground mb-4">
                    Assign each ticket to an attendee. The purchaser information will be used for the first ticket by default.
                  </p>
                  
                  <div className="space-y-4">
                    {Array.from({ length: ticket?.quantity }, (_, index) => {
                      const assignment = ticketAssignments?.[index] || {};
                      const ticketNumber = index + 1;
                      
                      return (
                        <div key={index} className="bg-muted rounded-lg p-4">
                          <h4 className="font-medium text-foreground mb-3">
                            Ticket #{ticketNumber}
                            {index === 0 && (
                              <span className="text-xs text-primary ml-2">(Purchaser)</span>
                            )}
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Input
                              label="First Name"
                              type="text"
                              placeholder="Enter first name"
                              value={assignment?.firstName || ''}
                              onChange={(e) => handleAssignmentChange(ticket?.id, index, 'firstName', e?.target?.value)}
                              error={errors?.[`${ticket?.id}_${index}_firstName`]}
                              required
                            />
                            
                            <Input
                              label="Last Name"
                              type="text"
                              placeholder="Enter last name"
                              value={assignment?.lastName || ''}
                              onChange={(e) => handleAssignmentChange(ticket?.id, index, 'lastName', e?.target?.value)}
                              error={errors?.[`${ticket?.id}_${index}_lastName`]}
                              required
                            />
                            
                            <Input
                              label="Email Address"
                              type="email"
                              placeholder="Enter email address"
                              value={assignment?.email || ''}
                              onChange={(e) => handleAssignmentChange(ticket?.id, index, 'email', e?.target?.value)}
                              error={errors?.[`${ticket?.id}_${index}_email`]}
                              description="Ticket will be sent to this email"
                              required
                            />
                            
                            <Input
                              label="Phone Number"
                              type="tel"
                              placeholder="Enter phone number"
                              value={assignment?.phone || ''}
                              onChange={(e) => handleAssignmentChange(ticket?.id, index, 'phone', e?.target?.value)}
                              error={errors?.[`${ticket?.id}_${index}_phone`]}
                              description="For event updates (optional)"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {tickets?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="Ticket" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No tickets to assign</p>
        </div>
      )}
    </div>
  );
};

export default TicketAssignment;